import { Check } from "lucide-react";
import { Container, Section, Heading, Lede, Eyebrow, LinkButton } from "../../components/ui";
import { Seo } from "../../lib/head";
import { FaqSection, CtaBand, faqPageJsonLd, type Faq } from "../../components/marketing";

/** What buyers evaluating any MRR tool should weigh — framed as criteria the
 *  reader applies themselves, with WeConsult Rx's answer. No claims are made
 *  about the competitor's product; the page captures "alternative" intent
 *  without asserting anything we haven't verified. */
const CRITERIA: { label: string; detail: string }[] = [
  { label: "End-to-end workflow", detail: "Chart ingest → clinical review → prescriber/DON e-sign → invoice, in one place — not just the review step." },
  { label: "Built-in e-signature", detail: "Prescriber and DON sign the recommendation bundle without exporting to a separate signing tool." },
  { label: "Invoicing", detail: "Invoices fire from the same workflow, the day you close the review." },
  { label: "EHR chart ingest", detail: "PointClickCare and other EHRs pulled via FHIR — no manual re-keying each month." },
  { label: "F-tag defensibility", detail: "Recommendations aligned to CMS F-tag guidance (F756–F881) and captured as a dated, signed record." },
  { label: "SNF + ASC + dialysis", detail: "One platform across medication regimen reviews and ambulatory-surgical and dialysis compliance." },
];

export interface ComparePageProps {
  competitor: string;
  path: string;
  title: string;
  description: string;
  h1: string;
}

export function ComparePage({ competitor, path, title, description, h1 }: ComparePageProps) {
  const faqs: Faq[] = [
    {
      q: `Is WeConsult Rx a good alternative to ${competitor}?`,
      a: `If you're looking for a platform that runs the whole monthly consultant-pharmacy engagement — chart ingest, rules-and-AI clinical review, prescriber and DON e-signature, and invoicing — WeConsult Rx is built for exactly that. The best way to compare is a side-by-side demo on a sample facility.`,
    },
    {
      q: `Can we migrate from ${competitor}?`,
      a: `Yes. WeConsult Rx ingests charts from PointClickCare and other EHRs via FHIR, so you start each review from live data rather than re-entering it. Our team helps you onboard facilities and pharmacists during evaluation.`,
    },
    {
      q: "What should we look for when evaluating MRR software?",
      a: "Whether it covers the entire monthly loop (not just the review), whether e-signature and invoicing are built in, how charts get in (manual vs. EHR ingest), how defensible the F-tag documentation is, and whether one platform covers SNF, ASC, and dialysis.",
    },
    {
      q: "Do demos use real patient data?",
      a: "No — every demo uses synthetic data only, never real resident PHI. WeConsult Rx is built for a HIPAA-conscious workflow with a BAA available.",
    },
  ];

  return (
    <>
      <Seo title={title} description={description} path={path} jsonLd={faqPageJsonLd(faqs)} />

      <Section className="pb-8">
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>Comparison</Eyebrow>
            <Heading level={1} className="mt-3">
              {h1}
            </Heading>
            <Lede className="mt-6">
              Evaluating alternatives to {competitor}? Here's how WeConsult Rx approaches the monthly
              consultant-pharmacy workflow — and the criteria worth weighing before you switch.
            </Lede>
            <div className="mt-8 flex flex-wrap gap-3">
              <LinkButton to="/demo">Book a comparison demo</LinkButton>
              <LinkButton to="/platform" variant="secondary">
                See the full workflow
              </LinkButton>
            </div>
          </div>
        </Container>
      </Section>

      <Section tone="soft">
        <Container>
          <Heading level={2}>What to look for in an MRR platform</Heading>
          <Lede className="mt-4">Use these as your evaluation checklist — here's how WeConsult Rx answers each.</Lede>
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {CRITERIA.map((c) => (
              <div key={c.label} className="flex gap-4 rounded-2xl border border-border bg-surface p-6">
                <span className="mt-0.5 inline-flex h-7 w-7 flex-none items-center justify-center rounded-full bg-forest-soft text-forest-700">
                  <Check className="h-4 w-4" aria-hidden />
                </span>
                <div>
                  <p className="font-semibold text-ink-900">{c.label}</p>
                  <p className="mt-1 text-[0.9375rem] leading-relaxed text-ink-600">{c.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      <FaqSection items={faqs} />

      <CtaBand
        title={`See how WeConsult Rx compares`}
        body="Book a side-by-side demo on a sample facility, or start with a free first-facility review."
        primary={{ to: "/demo", label: "Book a demo" }}
        secondary={{ to: "/pricing", label: "See pricing" }}
      />
    </>
  );
}
